import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';

const YouTubeInstructions = () => {
  return (
    <div className="min-h-screen bg-slate-950 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            YouTube Live Stream <span className="text-cyan-400">Setup Instructions</span>
          </h1>
          <p className="text-slate-400">Enable embedding for automatic live stream display</p>
        </div>

        {/* Important Notice */}
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-cyan-400 font-semibold mb-2">Why These Settings Matter</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Enabling these settings allows your live stream to automatically appear on the /live page 
                without manually updating video IDs each time you go live.
              </p>
            </div>
          </div>
        </div>

        {/* Step 1 */}
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardContent className="p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-white text-xl font-semibold mb-3">Enable Channel Embedding</h3>
                <div className="space-y-3 text-slate-300">
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Go to <strong className="text-white">YouTube Studio</strong> (youtube.com/studio)</span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Click <strong className="text-white">Settings</strong> (gear icon in the left sidebar)</span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Go to <strong className="text-white">Channel</strong> → <strong className="text-white">Advanced settings</strong></span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Scroll down and make sure <strong className="text-white">"Allow embedding"</strong> is checked ✓</span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Click <strong className="text-white">Save</strong></span>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 2 */}
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardContent className="p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-white text-xl font-semibold mb-3">Configure Live Stream Settings</h3>
                <div className="space-y-3 text-slate-300">
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>When creating/scheduling a live stream, click <strong className="text-white">Edit</strong></span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Under <strong className="text-white">Visibility</strong>, select <strong className="text-white">Public</strong></span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Scroll down to <strong className="text-white">More options</strong></span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Make sure <strong className="text-white">"Allow embedding"</strong> is turned ON for this stream</span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Save your changes</span>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3 */}
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardContent className="p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-white text-xl font-semibold mb-3">Verify It Works</h3>
                <div className="space-y-3 text-slate-300">
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Go live with your stream</span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Visit your <strong className="text-white">/live</strong> page</span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Click on the slideshow - the stream should appear automatically!</span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>If it doesn't work, use the <strong className="text-white">/live-admin</strong> page as a backup</span>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardContent className="p-8">
            <h3 className="text-white text-xl font-semibold mb-4">Troubleshooting</h3>
            <div className="space-y-4">
              <div className="bg-slate-950 p-4 rounded-lg border border-slate-800">
                <h4 className="text-cyan-400 font-semibold mb-2">Problem: "This video is unavailable"</h4>
                <p className="text-slate-300 text-sm mb-2"><strong>Solution:</strong></p>
                <ul className="text-slate-400 text-sm space-y-1 list-disc list-inside">
                  <li>Check that embedding is enabled in channel settings</li>
                  <li>Verify the stream visibility is set to Public</li>
                  <li>Make sure the stream is actually live</li>
                  <li>As a backup, use /live-admin to manually set the video ID</li>
                </ul>
              </div>

              <div className="bg-slate-950 p-4 rounded-lg border border-slate-800">
                <h4 className="text-cyan-400 font-semibold mb-2">Problem: Stream doesn't auto-update</h4>
                <p className="text-slate-300 text-sm mb-2"><strong>Solution:</strong></p>
                <ul className="text-slate-400 text-sm space-y-1 list-disc list-inside">
                  <li>This is normal - each new stream creates a new video ID</li>
                  <li>Option 1: Enable channel embedding (Steps 1-2 above)</li>
                  <li>Option 2: Use /live-admin to update manually before each stream</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="flex flex-wrap gap-4 justify-center">
          <a
            href="https://studio.youtube.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Open YouTube Studio
            <ExternalLink className="w-4 h-4 ml-2" />
          </a>
          <a
            href="/live-admin"
            className="inline-flex items-center bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Go to Live Admin
          </a>
          <a
            href="/live"
            className="inline-flex items-center bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            View Live Page
          </a>
          <a
            href="/"
            className="inline-flex items-center bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default YouTubeInstructions;
